import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import "./MultiSelectDropdown.css";

const MultiSelectDropdown = ({ options = [], value = [], onChange, placeholder = "Select" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  
  const normalized = useMemo(() => {
    return (Array.isArray(options) ? options : []).map((o) => ({
      label: o.label ?? String(o.value ?? o.id ?? ""),
      value: o.value ?? o.id ?? String(o.label ?? ""),
    }));
  }, [options]);

  const selectedCount = value?.length || 0;

  const toggleOption = (val) => {
    if (!Array.isArray(value)) return;
    if (value.includes(val)) {
      onChange?.(value.filter((v) => v !== val));
    } else {
      onChange?.([...(value || []), val]);
    }
  };

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen((s) => !s);

  // Close only when clicking outside dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        close();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Keyboard accessibility
  const onKeyDown = (e) => {
    if (!isOpen && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
      e.preventDefault();
      open();
      setActiveIndex(0);
      return;
    }
    if (!isOpen) return;
    if (e.key === "Escape") { e.preventDefault(); close(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((i) => (i + 1) % normalized.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((i) => (i - 1 + normalized.length) % normalized.length); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const opt = normalized[activeIndex];
      if (opt) toggleOption(opt.value);
    }
  };

  return (
    <div className="ms-wrapper">
      <button
        type="button"
        ref={btnRef}
        className={`ms-btn ${selectedCount > 0 ? "selected" : ""}`}
        onClick={toggle}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={`ms-label ${selectedCount > 0 ? "has-value" : ""}`}>
          {selectedCount > 0 ? `${placeholder} (${selectedCount})` : placeholder}
        </span>
        <ChevronDown size={16} />
      </button>

      {isOpen && (
        <div className="ms-menu" ref={menuRef} role="listbox" tabIndex={-1} onKeyDown={onKeyDown}>
          {normalized.map((opt, idx) => {
            const checked = value.includes(opt.value);
            return (
              <label key={opt.value} className={`ms-option ${idx === activeIndex ? "active" : ""}`} onMouseEnter={() => setActiveIndex(idx)}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleOption(opt.value)}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
